// Per-deck language presets. A deck names one by key. Voices are edge-tts
// (Microsoft neural) voice names; a preset without voices makes a plain
// text flashcard deck. Adding a language is adding an entry here.

export interface Language {
  name: string;
  voices: string[];
  /** BCP 47 prefix for the device's own speech fallback. */
  speech: string;
  labels: { text: string; reading: string; meaning: string };
  /** 'tones' colours syllables ending in a tone digit (jyutping, numbered pinyin). */
  readingStyle: 'tones' | 'plain';
}

const foreign = (text: string, reading = 'Pronunciation') => ({ text, reading, meaning: 'English' });

export const LANGUAGES: Record<string, Language> = {
  yue: {
    name: 'Cantonese',
    voices: ['zh-HK-HiuGaaiNeural', 'zh-HK-HiuMaanNeural', 'zh-HK-WanLungNeural'],
    speech: 'zh-HK',
    labels: foreign('Characters', 'Jyutping'),
    readingStyle: 'tones',
  },
  cmn: {
    name: 'Mandarin',
    voices: ['zh-CN-XiaoxiaoNeural', 'zh-CN-YunxiNeural', 'zh-CN-XiaoyiNeural'],
    speech: 'zh-CN',
    labels: foreign('Characters', 'Pinyin'),
    readingStyle: 'tones',
  },
  ja: {
    name: 'Japanese',
    voices: ['ja-JP-NanamiNeural', 'ja-JP-KeitaNeural'],
    speech: 'ja-JP',
    labels: foreign('Japanese', 'Reading'),
    readingStyle: 'plain',
  },
  ko: {
    name: 'Korean',
    voices: ['ko-KR-SunHiNeural', 'ko-KR-InJoonNeural'],
    speech: 'ko-KR',
    labels: foreign('Korean', 'Romanization'),
    readingStyle: 'plain',
  },
  fr: { name: 'French', voices: ['fr-FR-DeniseNeural', 'fr-FR-HenriNeural'], speech: 'fr', labels: foreign('French'), readingStyle: 'plain' },
  es: { name: 'Spanish', voices: ['es-ES-ElviraNeural', 'es-MX-JorgeNeural'], speech: 'es', labels: foreign('Spanish'), readingStyle: 'plain' },
  de: { name: 'German', voices: ['de-DE-KatjaNeural', 'de-DE-ConradNeural'], speech: 'de', labels: foreign('German'), readingStyle: 'plain' },
  it: { name: 'Italian', voices: ['it-IT-ElsaNeural', 'it-IT-DiegoNeural'], speech: 'it', labels: foreign('Italian'), readingStyle: 'plain' },
  pt: { name: 'Portuguese', voices: ['pt-BR-FranciscaNeural', 'pt-BR-AntonioNeural'], speech: 'pt', labels: foreign('Portuguese'), readingStyle: 'plain' },
  vi: { name: 'Vietnamese', voices: ['vi-VN-HoaiMyNeural', 'vi-VN-NamMinhNeural'], speech: 'vi', labels: foreign('Vietnamese'), readingStyle: 'plain' },
  none: {
    name: 'No audio (plain flashcards)',
    voices: [],
    speech: '',
    labels: { text: 'Front', reading: 'Extra', meaning: 'Back' },
    readingStyle: 'plain',
  },
};

export function language(key: string): Language {
  return LANGUAGES[key] ?? LANGUAGES.none;
}

export function hasAudio(key: string): boolean {
  return language(key).voices.length > 0;
}
